import { useState, useCallback, useEffect } from 'react';
import { AutoMovement } from '../../../../types';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { supabase } from '../../../../lib/supabase';
import { useLeads } from '../../../../contexts/leads/LeadsContext';

/** Formata número bruto para +55 11 99999-9999 */
function formatPhone(raw: string): string {
    const digits = raw.replace(/\D/g, '');
    if (digits.length >= 12) {
        // Ex: 5511987654321
        const country = digits.slice(0, 2);
        const ddd = digits.slice(2, 4);
        const num = digits.slice(4);
        const formatted = num.length === 9
            ? `${num.slice(0, 5)}-${num.slice(5)}`
            : `${num.slice(0, 4)}-${num.slice(4)}`;
        return `+${country} (${ddd}) ${formatted}`;
    }
    if (digits.length >= 10) {
        // Sem código do país
        const ddd = digits.slice(0, 2);
        const num = digits.slice(2);
        const formatted = num.length === 9
            ? `${num.slice(0, 5)}-${num.slice(5)}`
            : `${num.slice(0, 4)}-${num.slice(4)}`;
        return `(${ddd}) ${formatted}`;
    }
    return raw;
}


export const useAutoMovement = () => {
    const { currentOrganization } = useOrganization();
    const { funnelStages } = useLeads();
    const [movements, setMovements] = useState<AutoMovement[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch movements from Supabase
    const refreshMovements = useCallback(async () => {
        if (!currentOrganization?.id) return;

        setIsLoading(true);
        try {
            const [movementsResponse, instancesResponse, integrationsResponse] = await Promise.all([
                supabase
                    .from('auto_movements')
                    .select(`
                        id,
                        message,
                        mode,
                        channel_id,
                        stage_id,
                        created_at,
                        organization_id,
                        funnel_stages:stage_id (
                            id,
                            name
                        )
                    `)
                    .eq('organization_id', currentOrganization.id)
                    .order('created_at', { ascending: false }),
                supabase
                    .from('whatsapp_instances')
                    .select('id, instance_name, phone_number, integration_id')
                    .eq('organization_id', currentOrganization.id),
                supabase
                    .from('integrations')
                    .select('id, config, name')
                    .eq('organization_id', currentOrganization.id)
                    .eq('type', 'whatsapp')
            ]);

            if (movementsResponse.error) throw movementsResponse.error;

            // Mapa de integração pelo id
            const integByIntegId = new Map<string, any>();
            (integrationsResponse.data || []).forEach(integ => {
                integByIntegId.set(integ.id, integ);
            });

            // Mapa: whatsapp_instances.id → melhor nome de exibição
            const instancesMap = new Map<string, string>();
            (instancesResponse.data || []).forEach(inst => {
                // Tenta: phone_number da instância → display_number da integração → instance_name
                const integ = inst.integration_id ? integByIntegId.get(inst.integration_id) : null;
                const phoneNumber = inst.phone_number
                    || integ?.config?.display_number
                    || null;
                const display = phoneNumber
                    ? formatPhone(phoneNumber)
                    : (inst.instance_name || 'WhatsApp');
                instancesMap.set(inst.id, display);
            });

            const mappedMovements: AutoMovement[] = (movementsResponse.data || []).map(row => ({
                id: row.id,
                message: row.message,
                mode: row.mode as AutoMovement['mode'],
                channelId: row.channel_id,
                channelName: row.channel_id
                    ? instancesMap.get(row.channel_id) || 'Desconhecido'
                    : 'Todos os canais',
                stageId: row.stage_id,
                stageName: (row.funnel_stages as any)?.name || '',
                organizationId: row.organization_id,
                createdAt: row.created_at
            }));

            setMovements(mappedMovements);
        } catch (error) {
            console.error('[useAutoMovement] Error fetching movements:', error);
        } finally {
            setIsLoading(false);
        }
    }, [currentOrganization?.id]);

    // Load movements on mount
    useEffect(() => {
        refreshMovements();
    }, [refreshMovements]);

    const createMovement = useCallback(async (data: Omit<AutoMovement, 'id' | 'createdAt' | 'organizationId'>) => {
        if (!currentOrganization?.id) throw new Error('No organization');

        const { data: newRow, error } = await supabase
            .from('auto_movements')
            .insert({
                organization_id: currentOrganization.id,
                message: data.message,
                mode: data.mode,
                channel_id: data.channelId,
                stage_id: data.stageId
            })
            .select(`
                id,
                message,
                mode,
                channel_id,
                stage_id,
                created_at,
                organization_id,
                funnel_stages:stage_id (
                    id,
                    name
                )
            `)
            .single();

        if (error) throw error;

        const newMovement: AutoMovement = {
            id: newRow.id,
            message: newRow.message,
            mode: newRow.mode as AutoMovement['mode'],
            channelId: newRow.channel_id,
            channelName: data.channelName,
            stageId: newRow.stage_id,
            stageName: (newRow.funnel_stages as any)?.name || data.stageName || '',
            organizationId: newRow.organization_id,
            createdAt: newRow.created_at
        };

        setMovements(prev => [newMovement, ...prev]);
        return newMovement;
    }, [currentOrganization?.id]);

    const updateMovement = useCallback(async (id: string, data: Partial<AutoMovement>) => {
        const updateData: any = {};
        if (data.message !== undefined) updateData.message = data.message;
        if (data.mode !== undefined) updateData.mode = data.mode;
        if (data.channelId !== undefined) updateData.channel_id = data.channelId;
        if (data.stageId !== undefined) updateData.stage_id = data.stageId;
        updateData.updated_at = new Date().toISOString();

        const { error } = await supabase
            .from('auto_movements')
            .update(updateData)
            .eq('id', id);

        if (error) throw error;

        // Fetch fresh stage name if stageId changed
        let stageName = data.stageName;
        if (data.stageId) {
            const stage = funnelStages.find(s => s.id === data.stageId);
            stageName = stage?.name || stageName;
        }

        setMovements(prev => prev.map(m =>
            m.id === id ? { ...m, ...data, stageName: stageName || m.stageName } : m
        ));
    }, [funnelStages]);

    const deleteMovement = useCallback(async (id: string) => {
        const { error } = await supabase
            .from('auto_movements')
            .delete()
            .eq('id', id);

        if (error) throw error;

        setMovements(prev => prev.filter(m => m.id !== id));
    }, []);

    const duplicateMovement = useCallback(async (movement: AutoMovement) => {
        const newData = {
            message: `${movement.message} (Cópia)`,
            mode: movement.mode,
            channelId: movement.channelId,
            channelName: movement.channelName,
            stageId: movement.stageId,
            stageName: movement.stageName
        };
        return createMovement(newData);
    }, [createMovement]);

    return {
        movements,
        isLoading,
        refreshMovements,
        createMovement,
        updateMovement,
        deleteMovement,
        duplicateMovement
    };
};
